import React from 'react';
import { Link } from 'react-router-dom';
import { ProfileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderStepContext } from './orderStepContext';
import { StepSection } from './OrderProcessParts';
import { useQuantityFormatter } from './OrderProcessTables';
import { activeSupplierDraft, supplierDraftStatusKey } from './orderProcessFacts';
import { withLineDimensions } from '../orderProductDisplay';

/**
 * „Podsumowanie zamówienia” (mockup): who the order is for and where it goes —
 * number, customer, contact, delivery address — plus where the ZD draft stands.
 * The ZK number doubles as the offer number (the offer has no number of its
 * own), so there is no „Numer oferty”. The ZD link is only there for who may
 * open a ZD (the server blanks the id for everybody else).
 * Without a ZD the wording follows the current stock status (the same
 * `facts.missing` as „Na co czeka zamówienie”), never the mere absence of a
 * ZD: quantity grown after confirmation can leave goods missing with no ZD
 * („ekrany nie mogą sobie przeczyć”). The ZK card has no ZD control, so the
 * copy names the one that exists („Zamówienia do dostawców” → „Nowe…”).
 * The ZD shows its status (szkic do przejrzenia, wysłane…); a rejected draft
 * or a cancelled ZD counts as no ZD (`activeSupplierDraft`) and stays only as
 * a note under the shortage wording.
 * „Niezamówione w ZD” lists lines live ZD buy only in part (a split between
 * suppliers or a ZD quantity lowered later, decision 2026-10-19).
 */
export default function OrderSummaryCard({ ctx }: { ctx: OrderStepContext }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const { order } = ctx;
  const missing = ctx.facts.missing;
  const customer = order.customer;
  const contact = [customer?.contactPerson, customer?.phone].filter(Boolean).join(' / ');
  const address = order.deliveryLocation || customer?.address || '';
  const shown = ctx.stockStatus?.supplierDraft;
  const draft = activeSupplierDraft(ctx.stockStatus);
  const statusKey = supplierDraftStatusKey(shown);
  const draftNumber = shown?.supplierOrderNumber || '';
  // „Niezamówione w ZD” names the product with its dimensions: the business always asks which size.
  const productsById = new Map((order.productsLoaded ? order.products : []).map((product) => [product.id, product]));
  const rows: Array<[string, React.ReactNode]> = [
    [t('orders:process.summary.number'), order.orderNumber],
    [t('orders:process.summary.customer'), customer?.name || '—'],
    [t('orders:process.summary.contact'), contact || '—'],
    [t('orders:process.summary.address'), address || '—'],
    [t('orders:process.summary.draft'), shown?.documents && shown.documents.length > 1 ? (
      <span data-testid="zk-summary-draft">{shown.documents.map(document => (
        <span key={document.supplierOrderNumber} className="zk-summary__note">
          {document.supplierOrderId ? <Link to={`/app/supplier-orders/${document.supplierOrderId}`}>{document.supplierOrderNumber}</Link> : document.supplierOrderNumber}
          {' · '}{supplierDraftStatusKey(document) ? t(supplierDraftStatusKey(document)!) : ''}
        </span>
      ))}</span>
    ) : draft ? (
      <span data-testid="zk-summary-draft">
        {[draftNumber, statusKey ? t(statusKey) : null].filter(Boolean).join(' · ')}
        {draft.supplierOrderId && (
          <> · <Link to={`/app/supplier-orders/${draft.supplierOrderId}`}>{t('orders:process.confirmed.openDraft')}</Link></>
        )}
      </span>
    ) : (
      <span data-testid="zk-summary-draft">
        {missing.length
          ? t('orders:process.summary.draftNoneMissing', {
            lines: missing.map((line) => `${line.name} ${quantity(line.quantity, line.unit)}`).join(', '),
          })
          : t('orders:process.summary.draftNone')}
        {statusKey && (
          <small className="zk-summary__note" data-testid="zk-summary-draft-history">
            {t('orders:process.summary.draftInactive', { number: draftNumber, status: t(statusKey) })}
          </small>
        )}
      </span>
    )],
    ...(shown?.partialLines?.length ? [[t('orders:process.summary.partial'), (
      <span data-testid="zk-summary-partial">{shown.partialLines.map(line => (
        <span key={line.orderProductId} className="zk-summary__note">
          {t('orders:process.summary.partialLine', { name: withLineDimensions(line.name, productsById.get(line.orderProductId)), covered: quantity(line.coveredQuantity, line.unit),
            ordered: quantity(line.orderedQuantity, line.unit), missing: quantity(line.remainingQuantity, line.unit) })}
        </span>
      ))}</span>
    )] as [string, React.ReactNode]] : []),
  ];
  return (
    <StepSection title={t('orders:process.summary.title')} icon={<ProfileOutlined />} testId="zk-order-summary">
      <dl className="zk-summary">
        {rows.map(([label, value]) => (
          <div className="zk-summary__row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </StepSection>
  );
}
